import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumbs from "../../../shared/components/Breadcrumbs";
import ServerSideGrid from "../../../shared/components/listview/ServerSideGrid";
import DefaultButton from "../../../shared/components/DefaultButton";
import {
  loadAssetManagementGetFormDataRequest,
  loadAssetManagementGetByIdRequest,
} from "../redux/Actions";

const AssetManagement = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [submittedFormId, setSubmittedFormId] = useState("");
  const [selectedRowCount, setSelectedRowCount] = useState(0);
  const { assetFormData, assetFormDataById, loading } = useSelector(
    (state) => state.AssetManagement
  );

  useEffect(() => {
    dispatch(loadAssetManagementGetFormDataRequest());
  }, []);

  const handleSubmit = () => {
    if (selectedFormId) {
      setSubmittedFormId(selectedFormId);
      setSelectedRowCount(0);
      dispatch(loadAssetManagementGetByIdRequest(selectedFormId));
    }
  };

  const formOptions = Array.isArray(assetFormData) ? assetFormData : [];

  // ── Export: mirrors Ticketing's handleExport ──────────────────────────────
  // Selected rows → manual CSV; nothing selected → AG Grid built-in export
  const handleExport = useCallback(() => {
    if (!gridRef.current) return;

    const selectedRows = gridRef.current.getSelectedRows();
    const formName =
      formOptions.find((f) => f.id === Number(submittedFormId))?.name ||
      "asset_management";

    if (selectedRows && selectedRows.length > 0) {
      // Build column list from backend cols + store col
      const allCols = [
        { headerName: "Store", field: "_store" },          // virtual — filled below
        ...(assetFormDataById?.columns ?? []).map((col) => ({
          headerName: col.display_name,
          field: col.field,
        })),
      ];

      const headers = allCols.map((c) => c.headerName);
      const fields  = allCols.map((c) => c.field);

      let csvContent = headers.join(",") + "\n";

      selectedRows.forEach((row) => {
        const rowValues = fields.map((field) => {
          // Resolve the virtual store col
          let value =
            field === "_store"
              ? `${row.store_name ?? ""} (${row.sap ?? ""})`.trim()
              : row[field] ?? "";

          if (typeof value === "string") {
            value = value.replace(/"/g, '""');
            if (value.includes(",") || value.includes("\n")) {
              value = `"${value}"`;
            }
          }
          return value;
        });
        csvContent += rowValues.join(",") + "\n";
      });

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `${formName.replace(/\s+/g, "_")}_selected_${new Date().toISOString().split("T")[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      // No selection — export everything visible in the grid
      gridRef.current.exportDataAsCsv({
        fileName: `${formName.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.csv`,
        processCellCallback: (params) => {
          if (params.column.getColId() === "store_name") {
            const name = params.node?.data?.store_name ?? "";
            const sap  = params.node?.data?.sap ?? "";
            return sap ? `${name} (${sap})` : name;
          }
          return params.value;
        },
      });
    }
  }, [submittedFormId, formOptions, assetFormDataById?.columns]);

  // ── Track selected row count (same pattern as Ticketing) ─────────────────
  const handleSelectionChanged = useCallback(() => {
    if (!gridRef.current) return;
    const selected = gridRef.current.getSelectedRows();
    setSelectedRowCount(selected ? selected.length : 0);
  }, []);

  const columnDefs = useMemo(() => {
    const storeCol = {
      headerName: "Store",
      field: "store_name",
      filter: "agTextColumnFilter",
      sortable: true,
      resizable: true,
      width: 200,
      valueGetter: (params) => {
        const name = params.data?.store_name ?? "";
        const sap  = params.data?.sap ?? "";
        return sap ? `${name} (${sap})` : name;
      },
      cellRenderer: (params) =>
        params.data?.store_id ? (
          <a href={`/stores/${params.data.store_id}`}>
            {params.data.store_name} ({params.data.sap})
          </a>
        ) : (
          params.value
        ),
    };

    const backendCols = (assetFormDataById?.columns ?? []).map((col) => ({
      headerName: col.display_name,
      field: col.field,
      filter: "agTextColumnFilter",
      sortable: true,
      resizable: true,
    }));

    return [storeCol, ...backendCols];
  }, [assetFormDataById?.columns]);

  // Deduplicate by submission_id
  const rowData = useMemo(() => {
    const seen = new Set();
    return (assetFormDataById?.data ?? []).filter((row) => {
      if (row.id === null) return false;
      const key = row.submission_id ?? row.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [assetFormDataById?.data]);

  const duplicateCount = useMemo(() => {
    const total = (assetFormDataById?.data ?? []).filter((r) => r.id !== null).length;
    return total - rowData.length;
  }, [assetFormDataById?.data, rowData]);

  return (
  <>
    <Row className="detailsPageHeader">
      <Col>
        <Breadcrumbs children={[{ label: "Asset Management" }]} />
      </Col>
    </Row>

    <Row className="mb-3 justify-content-center align-items-center g-2">
      <Col xs="auto">
        <select
          className="form-control"
          style={{ width: 400, height: 35 }}
          value={selectedFormId}
          onChange={(e) => setSelectedFormId(e.target.value)}
        >
          <option value="">-- Select a Form --</option>
          {formOptions.map((form) => (
            <option key={form.id} value={form.id}>
              {form.name}
            </option>
          ))}
        </select>
      </Col>
      <Col xs="auto">
        <DefaultButton
          buttonClass="default-btn-color"
          buttonTitle="Submit"
          actionBlock={handleSubmit}
        />
      </Col>
      {submittedFormId && (
        <Col xs="auto">
          <DefaultButton
            buttonClass="default-btn-color"
            buttonTitle={selectedRowCount > 0 ? `Export ${selectedRowCount}` : "Export"}
            actionBlock={handleExport}
          />
        </Col>
      )}
    </Row>

    <Row>
      <Card className="table-card">
        <Card.Body>
          {submittedFormId ? (
            <>
              {duplicateCount > 0 && (
                <div style={{ fontSize: 13, marginBottom: 8 }}>
                  <span className="text-warning">
                    ⚠ {duplicateCount} duplicate{" "}
                    {duplicateCount === 1 ? "entry" : "entries"} removed from view
                  </span>
                </div>
              )}
              <ServerSideGrid
                ref={gridRef}
                clientSide={true}
                columnDefs={columnDefs}
                rowData={rowData}
                loading={loading}
                paginationPageSize={80}
                tableHeight="calc(100vh - 320px)"
                defaultColDef={{ filter: "agTextColumnFilter", flex: 1, minWidth: 150 }}
                onSelectionChanged={handleSelectionChanged}
              />
            </>
          ) : (
            <p className="text-center">Please select a form.</p>
          )}
        </Card.Body>
      </Card>
    </Row>
  </>
);
};

export default AssetManagement;